/**
 * The platform card of a Saidiseire site page: the inside view of a site that
 * runs on our own platform (docs/saidiseire-plaan.md, item K).
 *
 * It stands where the WordPress card stands for an outside site, and answers
 * the same question - what does the site look like from the inside - without a
 * plugin, because the platform holds the answer already. Four figures first
 * (pages, restore points, form answers, visitors), then what needs a look,
 * then the facts one row each.
 *
 * Counts and dates only: nothing here opens a page, an answer or a visitor.
 * A site that was deleted on the platform says so in red and shows nothing
 * else; its monitor still checks the address, which is now somebody else's.
 */
import { CircleCheck, Loader2, Server } from "lucide-react";
import { useI18n } from "../../../../i18n/I18nProvider";
import {
  goodShare,
  platformFindingKey,
  platformFindingTone,
  platformPlanName,
  platformStatusKey,
  platformStatusTone,
  vitalTone,
  type PlatformFinding,
  type PlatformSiteView,
} from "../../../../lib/monitoringPlatform";
import { Btn, CardHead, StatusPill, fmtBytes, fmtDate, fmtDateTime, fmtNum } from "../../../ui";
import type { T } from "../words";
import { usePlatform } from "./usePlatform";

function Figures({ site, t }: { site: PlatformSiteView; t: T }) {
  const last = site.restorePoints.lastAt;
  return (
    <div className="crm-pstats grid-cols-2 lg:grid-cols-4">
      <div className="crm-pstat">
        <b className="crm-tnum">{fmtNum(site.pages.published)}</b>
        <span>{t("monitoring.platform.fig.pages")}</span>
        <span className="crm-pstat-sub">{t("monitoring.platform.fig.drafts", { n: site.pages.drafts })}</span>
      </div>
      <div className="crm-pstat">
        <b className="crm-tnum">{fmtNum(site.restorePoints.total)}</b>
        <span>{t("monitoring.platform.fig.restore")}</span>
        <span className="crm-pstat-sub" title={last ? fmtDateTime(last) : undefined}>
          {last ? t("monitoring.platform.fig.restoreLast", { when: fmtDate(last) }) : t("monitoring.platform.fig.restoreNone")}
        </span>
      </div>
      <div className="crm-pstat">
        <b className="crm-tnum">{fmtNum(site.forms.answers30d)}</b>
        <span>{t("monitoring.platform.fig.answers")}</span>
        <span className={site.forms.unread > 0 ? "crm-pstat-sub text-[var(--color-warning)]" : "crm-pstat-sub"}>
          {t("monitoring.platform.fig.unread", { n: site.forms.unread })}
        </span>
      </div>
      <div className="crm-pstat">
        <b className="crm-tnum">{fmtNum(site.traffic.visitors)}</b>
        <span>{t("monitoring.platform.fig.visitors")}</span>
        <span className="crm-pstat-sub">{t("monitoring.platform.fig.pageviews", { n: site.traffic.pageviews })}</span>
      </div>
    </div>
  );
}

function findingText(t: T, f: PlatformFinding): string {
  // The grace deadline is a date; every other detail is already words.
  const detail = f.code === "domain_grace" && f.detail ? fmtDate(f.detail) : (f.detail ?? "");
  // A suspension without a reason has its own sentence, so no empty brackets are drawn.
  const key = f.code === "site_suspended" && !f.detail ? "monitoring.platform.finding.site_suspended_plain" : platformFindingKey(f.code);
  return t(key, { detail });
}

function Findings({ findings, t }: { findings: PlatformFinding[]; t: T }) {
  if (findings.length === 0) {
    return (
      <p className="crm-sub flex items-center gap-2 text-[13px]">
        <CircleCheck className="size-4 text-[var(--color-success)]" />
        {t("monitoring.platform.noFindings")}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {findings.map((f) => (
        <li key={f.code} className="flex items-start gap-2.5 text-[13px]">
          <StatusPill tone={platformFindingTone(f.level)} className="shrink-0">
            {t(`monitoring.platform.level.${f.level}`)}
          </StatusPill>
          <span className="min-w-0 break-words pt-0.5">{findingText(t, f)}</span>
        </li>
      ))}
    </ul>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-1.5 text-[13px]">
      <span className="crm-mut w-[190px] shrink-0 max-[520px]:w-full">{label}</span>
      <span className="min-w-0 flex-1 break-words">{children}</span>
    </div>
  );
}

function Facts({ site, t, locale }: { site: PlatformSiteView; t: T; locale: string }) {
  const plan = platformPlanName(site, locale);
  return (
    <div>
      <Row label={t("monitoring.platform.row.owner")}>{t(`monitoring.platform.owner.${site.owner}`)}</Row>
      <Row label={t("monitoring.platform.row.plan")}>
        {/* A subscription that is not simply active shows its own state word: a raw code beats a hidden problem. */}
        {plan
          ? site.subscriptionStatus && site.subscriptionStatus !== "active"
            ? `${plan} · ${site.subscriptionStatus}`
            : plan
          : t("monitoring.platform.row.planNone")}
      </Row>
      <Row label={t("monitoring.platform.row.domains")}>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {site.domains.map((d) => (
            <span key={d.host} className="inline-flex items-center gap-1.5">
              <span className="crm-tnum">{d.host}</span>
              {d.primary ? <StatusPill tone="info">{t("monitoring.platform.row.primary")}</StatusPill> : null}
              {!d.verified ? <StatusPill tone="wt">{t("monitoring.platform.row.unverified")}</StatusPill> : null}
            </span>
          ))}
        </span>
      </Row>
      <Row label={t("monitoring.platform.row.lastChange")}>
        {site.pages.lastChangeAt ? fmtDateTime(site.pages.lastChangeAt) : "-"}
      </Row>
      <Row label={t("monitoring.platform.row.auto")}>
        {site.restorePoints.everyHours > 0
          ? t("monitoring.platform.row.autoEvery", { hours: site.restorePoints.everyHours })
          : t("monitoring.platform.row.autoOff")}
      </Row>
      <Row label={t("monitoring.platform.row.posts")}>
        <span className="crm-tnum">{fmtNum(site.posts)}</span>
      </Row>
      <Row label={t("monitoring.platform.row.media")}>
        {t("monitoring.platform.row.mediaValue", { files: site.media.files, size: fmtBytes(site.media.bytes) })}
      </Row>
      <Row label={t("monitoring.platform.row.members")}>
        <span className="crm-tnum">{fmtNum(site.members)}</span>
      </Row>
      <Row label={t("monitoring.platform.row.held")}>
        <span className="crm-tnum">{fmtNum(site.forms.held30d)}</span>
      </Row>
      <Row label={t("monitoring.platform.row.notFound")}>
        <span className="crm-tnum">{fmtNum(site.notFound7d)}</span>
      </Row>
      <Row label={t("monitoring.platform.row.speed")}>
        {site.vitals.length === 0 ? (
          <span className="crm-mut">{t("monitoring.platform.noVitals")}</span>
        ) : (
          <span className="flex flex-wrap gap-1.5">
            {site.vitals.map((v) => (
              <StatusPill key={v.metric} tone={vitalTone(v)}>
                {/* `total`, not `n`: the bracket holds a bare number, no counted word agrees with it. */}
                {t("monitoring.platform.vital", { metric: v.metric, share: goodShare(v) ?? 0, total: fmtNum(v.total) })}
              </StatusPill>
            ))}
          </span>
        )}
      </Row>
    </div>
  );
}

export function PlatformCard({ monitorId, t }: { monitorId: string; t: T }) {
  const { locale } = useI18n();
  const { data, failed, reload } = usePlatform(monitorId);

  const title = (
    <span className="inline-flex items-center gap-2">
      <Server className="size-4 text-muted-foreground" strokeWidth={1.8} />
      {t("monitoring.platform.title")}
    </span>
  );

  if (!data) {
    return (
      <section className="crm-card mb-4">
        <CardHead title={title} sub={t("monitoring.platform.sub")} />
        <div className="flex justify-center px-[26px] pb-6 text-muted-foreground">
          {failed ? (
            <div className="text-center">
              <p className="mb-3 text-[13px] text-[var(--color-destructive)]">{t("monitoring.platform.loadFailed")}</p>
              <Btn small onClick={() => void reload()}>
                {t("monitoring.retry")}
              </Btn>
            </div>
          ) : (
            <Loader2 className="size-5 animate-spin" />
          )}
        </div>
      </section>
    );
  }

  const site = data.site;
  const running = t("monitoring.platform.running", { version: data.platform.version, when: fmtDateTime(data.platform.startedAt) });
  const statusKey = site ? platformStatusKey(site.status) : null;

  return (
    <section className="crm-card mb-4">
      <CardHead
        title={title}
        sub={site ? `${site.name} · ${running}` : running}
        actions={
          site ? <StatusPill tone={platformStatusTone(site.status)}>{statusKey ? t(statusKey) : site.status}</StatusPill> : null
        }
      />
      {site ? (
        <>
          <Figures site={site} t={t} />
          <div className="grid gap-6 px-[26px] pb-5 pt-5 lg:grid-cols-2">
            <div>
              <p className="crm-prow-n mb-2">{t("monitoring.platform.findingsTitle")}</p>
              <Findings findings={data.findings} t={t} />
            </div>
            <div>
              <p className="crm-prow-n mb-1">{t("monitoring.platform.factsTitle")}</p>
              <Facts site={site} t={t} locale={locale} />
            </div>
          </div>
        </>
      ) : (
        <p className="px-[26px] pb-5 text-[13px] text-[var(--color-destructive)]">{t("monitoring.platform.gone")}</p>
      )}
    </section>
  );
}
